import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { UserCircle, ShieldCheck } from "@phosphor-icons/react";

/**
 * Two-step guest sign in: email/password → 6-digit code emailed → JWT.
 * On success: writes bi_token + bi_user to localStorage and calls onSuccess().
 * "Remember this device 30 days" stores a device_id under bi_guest_device.
 */
export default function GuestSignInCard({ onSuccess }) {
  const [step, setStep] = useState("password"); // password | verify
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pendingToken, setPendingToken] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  const finishSignIn = (data) => {
    localStorage.setItem("bi_token", data.token);
    localStorage.setItem("bi_user", JSON.stringify(data.user));
    if (data.device_id) localStorage.setItem("bi_guest_device", data.device_id);
    onSuccess?.(data.user);
    window.location.href = "/";
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const device_id = localStorage.getItem("bi_guest_device") || null;
      const { data } = await axios.post(`${API}/auth/guest/login`, { email, password, device_id });
      if (data?.needs_2fa) {
        setPendingToken(data.pending_token);
        setStep("verify");
        toast.success("We've emailed you a 6-digit code.");
      } else if (data?.token) {
        finishSignIn(data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed.");
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/guest/verify-2fa`, {
        pending_token: pendingToken, code: code.trim(), remember_device: remember,
      });
      finishSignIn(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect code.");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/guest/resend-2fa`, null, { params: { pending_token: pendingToken } });
      toast.success("New code sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {step === "password" ? (
        <motion.form
          key="pwd"
          onSubmit={submitPassword}
          data-testid="guest-signin-form"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
          className="mt-6 space-y-4"
        >
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
              <UserCircle size={14} weight="fill" /> Email
            </label>
            <input
              data-testid="guest-email"
              type="email" autoComplete="email" value={email}
              onChange={(e) => setEmail(e.target.value)} required
              className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Password</label>
            <input
              data-testid="guest-password"
              type="password" autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} required
              className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div className="flex items-center justify-between text-xs">
            <Link to="/forgot-password" className="text-blue-600 hover:underline" data-testid="guest-forgot-link">
              Forgot password?
            </Link>
          </div>
          <button
            data-testid="guest-signin-submit"
            type="submit" disabled={busy}
            className="w-full px-4 py-3 rounded-sm bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Continue"}
          </button>
        </motion.form>
      ) : (
        <motion.form
          key="verify"
          onSubmit={submitCode}
          data-testid="guest-2fa-form"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
          className="mt-6 space-y-4"
        >
          <div className="flex items-start gap-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-3">
            <ShieldCheck size={18} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
            <div>Check your inbox for a 6-digit code. It expires in 10 minutes.</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Verification code</label>
            <input
              data-testid="guest-2fa-code"
              type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
              autoComplete="one-time-code" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              required
              className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-lg font-mono tracking-[0.5em] text-center focus:outline-none focus:border-slate-900"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              data-testid="guest-2fa-remember"
              type="checkbox" checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            Remember this device for 30 days
          </label>
          <button
            data-testid="guest-2fa-submit"
            type="submit" disabled={busy || code.length !== 6}
            className="w-full px-4 py-3 rounded-sm bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60"
          >
            {busy ? "Verifying…" : "Verify and sign in"}
          </button>
          <div className="flex items-center justify-between text-xs">
            <button
              type="button"
              data-testid="guest-2fa-resend"
              onClick={resend} disabled={busy}
              className="text-blue-600 hover:underline disabled:opacity-60"
            >
              Resend code
            </button>
            <button
              type="button"
              data-testid="guest-2fa-back"
              onClick={() => { setStep("password"); setCode(""); }}
              className="text-slate-500 hover:text-slate-900"
            >
              Back
            </button>
          </div>
        </motion.form>
      )}
    </AnimatePresence>
  );
}
